import { useState, useEffect } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import Helpers from "@/Helpers";
import { toast } from "react-toastify";
import { apiMessage } from "@/utils/apiMessage";
import {
    buildColumnHeaderTsv,
    buildImportResultClipboardTsv,
    fieldKey,
} from "@/utils/importTestUsersTsv";

function buildTestStartUrl(code) {
    const base = (Helpers.Env("VITE_TEST_RUNNER_URL") || import.meta.env.VITE_TEST_RUNNER_URL || "").trim();
    if (!base || !code) return "";
    const normalizedBase = base.endsWith("/") ? base : `${base}/`;
    return `${normalizedBase}${code.replace(/^\//, "")}`;
}

function splitTsvLine(line, expectedColumns) {
    const parts = line.split("\t").map((part) => part.trim());
    while (parts.length < expectedColumns) {
        parts.push("");
    }
    return parts.slice(0, expectedColumns);
}

function fieldSequence(field, index) {
    return field.sequence ?? field.Sequence ?? index + 1;
}

/** All CTT request fields in configured sequence order (includes proctor-provided). */
function getCttFields(cttData) {
    const fields = cttData?.fields ?? cttData?.Fields ?? [];
    return [...fields]
        .filter((f) => fieldKey(f))
        .sort((a, b) => fieldSequence(a, 0) - fieldSequence(b, 0));
}

function isProctorProvided(field) {
    return !!(field.proctorProvided ?? field.ProctorProvided);
}

function parseTabularImport(text, cttData) {
    const cttFields = getCttFields(cttData);
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter((line) => line.length > 0);

    if (lines.length === 0) {
        return { users: [], parseError: null };
    }

    const expectedColumnCount = cttFields.length;
    if (expectedColumnCount === 0) {
        return { users: [], parseError: "No fields configured for this test type." };
    }

    let dataLines = lines;

    // Skip a header row when pasted values match field labels
    const firstParts = splitTsvLine(lines[0], expectedColumnCount);
    if (
        cttFields.every((field, index) =>
            firstParts[index]?.toLowerCase() === fieldKey(field).toLowerCase()
        )
    ) {
        dataLines = lines.slice(1);
    }

    if (dataLines.length === 0) {
        return { users: [], parseError: null };
    }

    const users = [];
    for (let lineIndex = 0; lineIndex < dataLines.length; lineIndex++) {
        const parts = splitTsvLine(dataLines[lineIndex], expectedColumnCount);

        const userObject = {};
        cttFields.forEach((field, index) => {
            userObject[fieldKey(field)] = parts[index] || "";
        });
        users.push(userObject);
    }

    return { users, parseError: null };
}

export default function ImportTestUsersModal({ isOpen, onClose, cttId, updateSpinner }) {
    const [isImporting, setIsImporting] = useState(false);
    const [cttData, setCttData] = useState(null);
    const [testUserData, setTestUserData] = useState([]);
    const [parseError, setParseError] = useState(null);

    // Fetch CTT data when modal opens
    useEffect(() => {
        if (isOpen && cttId) {
            fetchCttData();
        }
    }, [isOpen, cttId]);

    async function fetchCttData() {
        if (updateSpinner) updateSpinner(true);
        try {
            const resp = await RightTimeApi.getAsync(`/CompanyTestType/${cttId}`);
            if (resp && resp.ok) {
                setCttData(resp.jsonData || resp.data);
            } else {
                toast.error("Failed to load Company Test Type data");
            }
        } catch (error) {
            console.error("Error fetching CTT data:", error);
            toast.error("Error loading Company Test Type data");
        } finally {
            if (updateSpinner) updateSpinner(false);
        }
    }

    function applyImportedText(data) {
        setParseError(null);
        if (!data?.trim()) {
            setTestUserData([]);
            return;
        }

        if (!cttData) {
            setParseError("Test type fields are still loading — wait a moment and paste again.");
            return;
        }

        try {
            const { users, parseError: importParseError } = parseTabularImport(data, cttData);
            if (importParseError) {
                setParseError(importParseError);
                setTestUserData([]);
                return;
            }
            setTestUserData(users);
        } catch (error) {
            console.error("Parsing error:", error);
            setParseError(error.message);
            setTestUserData([]);
        }
    }

    function onNewTestUserData(e) {
        applyImportedText(e.target.value);
    }

    if (!isOpen) {
        return null;
    }

    function handleClose() {
        if (!isImporting) {
            setCttData(null);
            setTestUserData([]);
            setParseError(null);
            onClose();
        }
    }

    async function handleImport() {
        setIsImporting(true);
        if (updateSpinner) updateSpinner(true);
        try {
            // Create JSON object with cttId and users collection
            const importData = {
                cttId: cttId,
                users: testUserData
            };

            console.log("Importing test users for CTT ID:", cttId, importData);

            // Call the import API endpoint
            const resp = await RightTimeApi.postAsync(`/CompanyTestType/${cttId}/Import`, importData);

            if (resp && resp.ok) {
                // Process the returned tests collection and create TSV for Excel
                const responseData = resp.jsonData || resp.data;
                console.log("API Response:", responseData);

                if (responseData && responseData.tests && responseData.tests.length > 0) {
                    console.log("Tests from response:", responseData.tests);

                    // Filter out any header rows (where values match field names or look like headers)
                    const actualTestData = responseData.tests.filter(test => {
                        // Skip if this looks like a header row - check multiple patterns
                        const cttFieldNames = cttFields.map(fieldKey);

                        // Pattern 1: Values exactly match field names
                        const exactMatch = cttFieldNames.every(fieldName =>
                            test[fieldName] === fieldName
                        );

                        // Pattern 2: Check if Code field looks like a header (contains "Code" or "code")
                        const codeField = test['Code'] || '';
                        const codeIsHeader = codeField.toLowerCase().includes('code');

                        // Pattern 3: Check if multiple fields contain their own names
                        const multipleHeaderFields = cttFieldNames.filter(fieldName =>
                            test[fieldName] && test[fieldName].toLowerCase().includes(fieldName.toLowerCase())
                        ).length >= 2;

                        const isHeaderRow = exactMatch || codeIsHeader || multipleHeaderFields;

                        console.log(`Test ${test['Code']}: exactMatch=${exactMatch}, codeIsHeader=${codeIsHeader}, multipleHeaderFields=${multipleHeaderFields}, isHeaderRow=${isHeaderRow}`);

                        return !isHeaderRow;
                    });

                    console.log("Filtered test data:", actualTestData);

                    const tsvContent = buildImportResultClipboardTsv({
                        tests: actualTestData,
                        importedRows: testUserData,
                        cttFields,
                        testUrlForCode: buildTestStartUrl,
                    });
                    console.log("Generated TSV content:", tsvContent);

                    // Copy to clipboard
                    try {
                        await navigator.clipboard.writeText(tsvContent);
                        console.log("Successfully copied to clipboard");
                        toast.success(`Successfully imported ${testUserData.length} test users and copied data to clipboard for Excel`);
                    } catch (clipboardError) {
                        console.error("Failed to copy to clipboard:", clipboardError);
                        toast.success(`Successfully imported ${testUserData.length} test users (clipboard copy failed)`);
                    }
                } else {
                    console.log("No tests found in response or responseData is null");
                    console.log("Response structure:", Object.keys(responseData || {}));
                    toast.success(`Successfully imported ${testUserData.length} test users (no test data returned for clipboard)`);
                }

                // Reset and close modal on success
                setTestUserData([]);
                setParseError(null);
                onClose();
            } else {
                const errBody = resp?.jsonData ?? {};
                const detail = apiMessage(resp);
                const missing = errBody.missing?.length ? ` Missing: ${errBody.missing.join(", ")}` : "";
                const extra = errBody.extra?.length ? ` Extra: ${errBody.extra.join(", ")}` : "";
                toast.error(detail ? `${detail}${missing}${extra}` : "Failed to import test users");
            }
        } catch (error) {
            console.error("Error importing test users:", error);
            toast.error("Failed to import test users");
        } finally {
            setIsImporting(false);
            if (updateSpinner) updateSpinner(false);
        }
    }

    const cttFields = getCttFields(cttData);

    async function copyColumnHeaders() {
        if (cttFields.length === 0) return;
        try {
            await navigator.clipboard.writeText(buildColumnHeaderTsv(cttFields));
            toast.success("Copied Excel column headers to clipboard");
        } catch (clipboardError) {
            console.error("Failed to copy column headers:", clipboardError);
            toast.error("Could not copy column headers to clipboard");
        }
    }

    const footer = (
        <div className="footerRight">
            <button className="btn btn-secondary" onClick={handleClose} disabled={isImporting}>
                Cancel
            </button>
            <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={isImporting || !cttData || testUserData.length === 0 || cttFields.length === 0}
            >
                {isImporting ? 'Importing...' : 'Import'}
            </button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            title="Import Test Users"
            onClose={handleClose}
            className="importTestUsersModal"
            footer={footer}
        >
            <div>
                {cttData && (
                    <div>
                        {cttFields.length > 0 ? (
                            <div>
                                <div className="tableWrapper dimensionTable">
                                    <table className="table">
                                        <caption>
                                          <span className="title">Fields (paste columns in this order)</span>
                                        </caption>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Name</th>
                                                <th>Required</th>
                                                <th>Proctor Provided</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cttFields.map((field, index) => (
                                                <tr key={field.id ?? field.Id}>
                                                    <td>{index + 1}</td>
                                                    <td>{fieldKey(field)}</td>
                                                    <td>{(field.required ?? field.Required) ? 'Yes' : 'No'}</td>
                                                    <td>{isProctorProvided(field) ? 'Yes' : 'No'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm copyExcelColumnsButton"
                                    onClick={copyColumnHeaders}
                                    title="Copy tab-separated column headers to clipboard for Excel"
                                >
                                    Copy Excel columns
                                </button>
                                {testUserData.length === 0 ? (
                                    <div>
                                        <p className="importHelpText">
                                            Copy/paste tab-separated rows from Excel into the box below.
                                        </p>
                                        <textarea
                                            className="excel-import-drop-target"
                                            placeholder="Paste Excel data here"
                                            onChange={onNewTestUserData}
                                            rows={6}
                                        />
                                        {parseError && (
                                            <div className="alert alert-danger importParseError">
                                                {parseError}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <p>If the data is incorrect, <button type="button" className="restartImportLink" onClick={() => {setTestUserData([]); setParseError(null);}}>click here to restart</button> and paste the data again.</p>

                                        <div className="tableWrapper factTable scrollable horizontal parsedImportTable">
                                            <table className="table">
                                                <caption>
                                                  <span className="title">Parsed Test User Data ({testUserData.length} users)</span>
                                                </caption>
                                                <thead>
                                                    <tr>
                                                        {cttFields.map((field) => (
                                                            <th key={field.id ?? field.Id}>{fieldKey(field)}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {testUserData.map((user, index) => (
                                                        <tr key={index}>
                                                            {cttFields.map((field) => (
                                                                <td key={field.id ?? field.Id}>{user[fieldKey(field)] || ''}</td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p>No fields found for this Company Test Type.</p>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}

